import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type JSX, type KeyboardEvent } from 'react';

import { Avatar } from '../components/Avatar.js';
import { AvatarLia } from '../lia/AvatarLia.js';
import { cn } from '../lib/cn.js';
import {
  filtrerMentions,
  insererMention,
  mentionEnCours,
  mentionsAffichees,
  versAffichage,
  versStockage,
  type EntreeDeMention,
} from './mentions.js';

export interface ChampAvecMentionsProps {
  /** Le texte STOCKE (`@[p-celia]`). Le champ affiche les noms et rend le texte stocke par `onChange`. */
  value: string;
  onChange: (texteStocke: string) => void;
  entrees: readonly EntreeDeMention[];
  /** Entree (sans Maj) envoie ; Maj+Entree saute une ligne. Absent : Entree saute une ligne. */
  onEnvoyer?: (() => void) | undefined;
  placeholder?: string | undefined;
  ariaLabel: string;
  autoFocus?: boolean | undefined;
  /** Au-dela de ce nombre de lignes le champ defile au lieu de grandir (defaut 10). */
  lignesMax?: number | undefined;
  disabled?: boolean | undefined;
  maxLength?: number | undefined;
  className?: string | undefined;
}

/**
 * UNE TEXTAREA QUI PROPOSE LES PERSONNES SUR `@` : la liste s'ouvre sur `@`,
 * filtre sur le prefixe tape, Haut et Bas deplacent, Entree choisit, Echap
 * ferme. Ce qui est AFFICHE porte les noms (« @Celia ») ; ce qui est STOCKE
 * (`value`, `onChange`) porte les jetons (`@[p-celia]`), par la source.
 *
 * LA HAUTEUR EST AUTOMATIQUE, pour toutes les surfaces : une ligne a vide, elle
 * grandit avec le texte jusqu'a `lignesMax`, puis defile. Elle est mesuree a
 * chaque changement du texte AFFICHE (le vidage apres envoi et le changement
 * de brouillon passent par lui), et refaite quand la LARGEUR change : un champ
 * mesure alors qu'il etait etroit ou cache garderait sinon une hauteur fausse.
 *
 * UN TEXTE VENU DU PARENT (le brouillon d'une conversation, au montage ou en
 * cours de route) place le curseur a sa FIN, et le bas du texte est visible.
 */
export function ChampAvecMentions({
  value, onChange, entrees, onEnvoyer, placeholder, ariaLabel, autoFocus, lignesMax = 10, disabled, maxLength = 20_000, className,
}: ChampAvecMentionsProps): JSX.Element {
  const textarea = useRef<HTMLTextAreaElement>(null);
  const calque = useRef<HTMLDivElement>(null);
  const listeId = useId();
  const [affiche, setAffiche] = useState(() => versAffichage(value, entrees));
  const [curseur, setCurseur] = useState(0);
  const [ferme, setFerme] = useState(false);
  const [actif, setActif] = useState(0);
  const [curseurAPoser, setCurseurAPoser] = useState<number | null>(null);
  const largeurMesuree = useRef(0);
  // Le texte vient du parent (brouillon d'une conversation) et le curseur doit en rejoindre la FIN, mais
  // `setSelectionRange` donne le focus a un champ qui ne l'a pas (Safari) : sans focus, on attend le prochain.
  const finAPoser = useRef(value !== '');

  // La hauteur suit le texte affiche. Une largeur nulle est un champ cache : rien a mesurer.
  const mesurer = (): void => {
    const champ = textarea.current;
    if (champ === null || champ.clientWidth === 0) return;
    const style = getComputedStyle(champ);
    const taille = parseFloat(style.fontSize);
    const ligneBrute = parseFloat(style.lineHeight);
    const ligne = Number.isFinite(ligneBrute) ? ligneBrute : (Number.isFinite(taille) ? taille : 14) * 1.5;
    const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
    const defilement = champ.scrollTop;
    champ.style.overflowY = 'hidden';
    champ.style.height = 'auto';
    const { hauteur, defile } = hauteurDuChamp({ scrollHeight: champ.scrollHeight, ligne, padding, lignesMax });
    champ.style.height = `${hauteur}px`;
    champ.style.overflowY = defile ? 'auto' : 'hidden';
    if (defile) {
      // Le curseur reste visible : en fin de texte on suit le bas, sinon on garde la position.
      const finDeTexte = finAPoser.current || (champ.selectionStart === champ.value.length && champ.selectionEnd === champ.value.length);
      champ.scrollTop = finDeTexte ? champ.scrollHeight : defilement;
    }
    if (calque.current !== null) calque.current.scrollTop = champ.scrollTop;
  };
  const mesurerCourant = useRef(mesurer);
  mesurerCourant.current = mesurer;

  useLayoutEffect(() => { mesurerCourant.current(); }, [affiche, lignesMax]);

  useEffect(() => {
    const champ = textarea.current;
    if (champ === null || typeof ResizeObserver === 'undefined') return;
    const observateur = new ResizeObserver((entrees) => {
      const largeur = entrees[entrees.length - 1]?.contentRect.width ?? 0;
      if (largeur <= 0 || largeur === largeurMesuree.current) return;
      largeurMesuree.current = largeur;
      mesurerCourant.current();
    });
    observateur.observe(champ);
    return () => observateur.disconnect();
  }, []);

  // Le parent a change la valeur stockee (vidage apres envoi, brouillon restaure) : on re-affiche AVANT
  // que le navigateur peigne, sinon le texte envoye reste visible une image et la saisie suivante s y colle.
  // Un texte non vide venu du parent met le curseur a sa fin : tout de suite si le champ a le focus.
  useLayoutEffect(() => {
    if (versStockage(affiche, entrees) === value) return;
    const suivant = versAffichage(value, entrees);
    setAffiche(suivant);
    if (suivant === '') { finAPoser.current = false; return; }
    finAPoser.current = true;
    if (document.activeElement === textarea.current) setCurseurAPoser(suivant.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, entrees]);

  useLayoutEffect(() => {
    if (curseurAPoser === null || textarea.current === null) return;
    textarea.current.setSelectionRange(curseurAPoser, curseurAPoser);
    finAPoser.current = false;
    setCurseurAPoser(null);
  }, [curseurAPoser]);

  // Le texte present au montage (autoFocus compris) ou en attente depuis un changement de brouillon : la fin au focus.
  const poserLaFin = (): void => {
    const champ = textarea.current;
    if (champ === null || !finAPoser.current) return;
    finAPoser.current = false;
    const fin = champ.value.length;
    champ.setSelectionRange(fin, fin);
    setCurseur(fin);
    if (champ.scrollHeight > champ.clientHeight) champ.scrollTop = champ.scrollHeight;
  };
  useLayoutEffect(() => {
    if (document.activeElement === textarea.current) poserLaFin();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enCours = ferme ? null : mentionEnCours(affiche, curseur, entrees);
  const pastilles = useMemo(() => mentionsAffichees(affiche, entrees), [affiche, entrees]);
  const propositions = useMemo(() => enCours === null ? [] : filtrerMentions(entrees, enCours.prefixe), [enCours?.prefixe, entrees, enCours === null]);
  const ouverte = propositions.length > 0;

  function poser(texte: string, position: number, rouvrir = true): void {
    setAffiche(texte);
    setCurseur(position);
    // La liste ne se rouvre qu'a un NOUVEAU `@` dans ce qui vient d'etre ajoute
    // (tape, colle ou propose par le clavier), jamais sur une pastille deja la (R2).
    if (rouvrir && aRouvrir(affiche, texte, position)) setFerme(false);
    setActif(0);
    onChange(versStockage(texte, entrees));
  }

  function choisir(entree: EntreeDeMention): void {
    const resultat = insererMention(affiche, curseur, entree);
    poser(resultat.texte, resultat.curseur, false);
    // La personne est choisie : la liste se FERME (R2).
    setFerme(true);
    setCurseurAPoser(resultat.curseur);
    textarea.current?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLTextAreaElement>): void {
    // RETOUR ARRIERE juste apres une pastille (ou apres son espace) : la
    // mention part d'un coup, jamais lettre a lettre (un nom ampute ne serait
    // plus une mention, et resterait en texte).
    if (event.key === 'Backspace' && textarea.current !== null && textarea.current.selectionStart === textarea.current.selectionEnd) {
      const position = textarea.current.selectionStart;
      const visee = pastilles.find((m) => m.fin === position || (m.fin + 1 === position && affiche[m.fin] === ' '));
      if (visee !== undefined) {
        event.preventDefault();
        const texte = affiche.slice(0, visee.debut) + affiche.slice(position);
        poser(texte, visee.debut, false);
        setCurseurAPoser(visee.debut);
        return;
      }
    }
    if (ouverte) {
      if (event.key === 'ArrowDown') { event.preventDefault(); setActif((i) => (i + 1) % propositions.length); return; }
      if (event.key === 'ArrowUp') { event.preventDefault(); setActif((i) => (i - 1 + propositions.length) % propositions.length); return; }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        const entree = propositions[actif] ?? propositions[0];
        if (entree !== undefined) choisir(entree);
        return;
      }
      if (event.key === 'Escape') { event.preventDefault(); setFerme(true); return; }
    }
    if (event.key === 'Enter' && !event.shiftKey && onEnvoyer !== undefined && !event.nativeEvent.isComposing) {
      event.preventDefault();
      onEnvoyer();
    }
  }

  // LE CALQUE DES PASTILLES (R2) : derriere la textarea, le meme texte, a la
  // meme place, ou chaque mention choisie porte le fond et le lisere de sa
  // pastille. C'est la TEXTAREA qu'on lit : son texte est visible et la
  // selection est celle du navigateur (P5 du 06/10/2026 : un texte transparent
  // devant une selection opaque effacait ce qu'on surlignait). Le calque, lui,
  // a un texte transparent : il ne dessine que les fonds. Meme police, meme
  // graisse : la pastille reste sous les lettres de la mention.
  const morceaux: Array<{ texte: string; mention?: EntreeDeMention }> = [];
  let pos = 0;
  for (const m of pastilles) {
    if (m.debut > pos) morceaux.push({ texte: affiche.slice(pos, m.debut) });
    morceaux.push({ texte: affiche.slice(m.debut, m.fin), mention: m.entree });
    pos = m.fin;
  }
  if (pos < affiche.length) morceaux.push({ texte: affiche.slice(pos) });

  return <div className={cn('relative min-w-0 flex-1', className)}>
    <div ref={calque} aria-hidden="true" data-calque-mentions
      className="pointer-events-none absolute inset-0 overflow-hidden whitespace-pre-wrap break-words py-[3px] text-sm leading-[21px] text-transparent">
      {morceaux.map((m, i) => m.mention === undefined
        ? <span key={i}>{m.texte}</span>
        : <span key={i} data-pastille-mention={m.mention.genre}
          className={cn('rounded-sm ring-1 ring-inset ring-accent-tint-border', m.mention.genre === 'lia' ? 'bg-accent-soft' : 'bg-accent-tint')}>{m.texte}</span>)}
      {/* Une fin de ligne : la derniere ligne vide garde sa hauteur. */}
      {affiche.endsWith('\n') ? <span>{' '}</span> : null}
    </div>
    <textarea
      ref={textarea}
      value={affiche}
      rows={1}
      disabled={disabled}
      maxLength={maxLength}
      autoFocus={autoFocus}
      placeholder={placeholder}
      aria-label={ariaLabel}
      aria-autocomplete="list"
      aria-expanded={ouverte}
      aria-controls={ouverte ? listeId : undefined}
      aria-activedescendant={ouverte ? `${listeId}-${actif}` : undefined}
      role="combobox"
      onChange={(event) => poser(event.target.value, event.target.selectionStart ?? event.target.value.length)}
      onFocus={poserLaFin}
      onSelect={(event) => setCurseur((event.target as HTMLTextAreaElement).selectionStart ?? 0)}
      onKeyDown={onKeyDown}
      onScroll={(event) => { if (calque.current) calque.current.scrollTop = (event.target as HTMLTextAreaElement).scrollTop; }}
      className="relative block min-h-7 w-full resize-none bg-transparent py-[3px] text-sm leading-[21px] text-ink-900 caret-ink-900 outline-none placeholder:text-ink-400 disabled:opacity-50"
    />
    {ouverte && <ul id={listeId} role="listbox" aria-label="Personnes a mentionner"
      className="absolute bottom-full left-0 z-20 mb-1 w-72 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-border bg-surface py-1 shadow-lg">
      {propositions.map((entree, index) => <li key={entree.id} id={`${listeId}-${index}`} role="option" aria-selected={index === actif}
        onMouseDown={(event) => { event.preventDefault(); choisir(entree); }}
        onMouseEnter={() => setActif(index)}
        className={cn('flex min-h-8 cursor-pointer items-center gap-2 px-3 py-1 text-sm', index === actif ? 'bg-surface-3' : '')}>
        {/* L'avatar du fil (FilDeConversation) : Lia en lime, une personne en initiales, cerclee pour rester lisible sur la ligne active. */}
        {entree.genre === 'lia'
          ? <AvatarLia size={24} />
          : <Avatar size={24} aria-hidden="true" src={entree.photoURL} initials={entree.genre === 'personne' ? initialesDe(entree.libelle) : '@'}
            className="bg-surface-3 text-ink-700 ring-1 ring-inset ring-border" />}
        <span className="min-w-0 flex-1 truncate font-medium text-ink-900">{libelleDansLaListe(entree)}</span>
      </li>)}
    </ul>}
  </div>;
}

/**
 * LE NOM D'UNE ENTREE DANS LA LISTE (P3 et P4 du 06/10/2026) : avatar et nom,
 * rien d'autre ; ni en-tete, ni e-mail, ni phrase d'aide. Lia s'y lit comme
 * une personne (« Lia ») ; seul `@tous` garde son arobase, sans quoi « tous »
 * ne dirait pas ce qu'il fait. Exporte pour le test.
 */
export function libelleDansLaListe(entree: Pick<EntreeDeMention, 'genre' | 'libelle'>): string {
  return entree.genre === 'tous' ? `@${entree.libelle}` : entree.libelle;
}

/**
 * LA REGLE DE HAUTEUR DU CHAMP : le contenu mesure (`scrollHeight`, remplissage
 * compris) borne entre une ligne et `lignesMax` lignes, remplissage compris.
 * Au-dela du plafond le champ defile. Pure, exportee pour le test.
 */
export function hauteurDuChamp({ scrollHeight, ligne, padding, lignesMax }: {
  scrollHeight: number; ligne: number; padding: number; lignesMax: number;
}): { hauteur: number; defile: boolean } {
  const mini = ligne + padding;
  const maxi = Math.max(1, lignesMax) * ligne + padding;
  return { hauteur: Math.min(Math.max(scrollHeight, mini), maxi), defile: scrollHeight > maxi };
}

/**
 * Le texte ajoute entre deux etats contient-il un `@` ? Le segment ajoute est
 * celui qui finit au curseur, de la longueur gagnee. Exporte pour le test.
 */
export function aRouvrir(avant: string, apres: string, curseur: number): boolean {
  const gagne = apres.length - avant.length;
  if (gagne <= 0) return false;
  return apres.slice(Math.max(0, curseur - gagne), curseur).includes('@');
}

/** Deux initiales d'un nom (« Celia Dupont » : CD ; « celia@taki.lu » : C). */
export function initialesDe(nom: string): string {
  const mots = nom.trim().split(/[\s@._-]+/u).filter(Boolean);
  const lettres = mots.slice(0, 2).map((m) => m[0] ?? '').join('');
  return lettres.toLocaleUpperCase('fr') || '?';
}
